import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useSite } from "@/context/SiteContext";

export default function IndustriesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const site = useSite();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/industries");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/admin/industries", { name: name.trim(), order: items.length + 1 });
      setName("");
      await load();
      await site.reload();
      toast.success("Industry added.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const rename = async (id, newName) => {
    try {
      await api.patch(`/admin/industries/${id}`, { name: newName });
      await load();
      await site.reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this industry? Features tagged with it will lose this tag.")) return;
    try {
      await api.delete(`/admin/industries/${id}`);
      await load();
      await site.reload();
      toast.success("Industry deleted.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="industries-panel" className="space-y-6">
      <Header title="Industries" subtitle="Verticals that features can be tagged to." />
      <form onSubmit={add} className="flex gap-2 max-w-xl">
        <Input
          data-testid="industry-input-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Aviation"
        />
        <Button data-testid="industry-add" type="submit" className="bg-[#84CC16] text-[#0A0A0A] hover:bg-[#65A30D] font-semibold">
          Add industry
        </Button>
      </form>

      <div className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
        {loading && <div className="p-4 text-sm text-neutral-500">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="p-6 text-sm text-neutral-500">No industries yet.</div>
        )}
        {items.map((it) => (
          <Row key={it.id} item={it} onRename={rename} onRemove={remove} />
        ))}
      </div>
    </div>
  );
}

function Row({ item, onRename, onRemove }) {
  const [val, setVal] = useState(item.name);
  return (
    <div className="px-4 py-3 flex items-center gap-3" data-testid={`industry-row-${item.slug}`}>
      <Input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => val !== item.name && onRename(item.id, val)}
        className="max-w-md"
      />
      <span className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500 flex-1">
        {item.slug}
      </span>
      <button
        type="button"
        onClick={() => onRemove(item.id)}
        data-testid={`industry-delete-${item.slug}`}
        className="text-xs font-semibold text-red-600 hover:text-red-700 px-2 py-1 rounded-md hover:bg-red-50"
      >
        Delete
      </button>
    </div>
  );
}

export const Header = ({ title, subtitle, right }) => (
  <div className="flex items-end justify-between gap-4 flex-wrap">
    <div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-neutral-950">
        {title}
      </h1>
      {subtitle && <p className="text-sm text-neutral-600 mt-1">{subtitle}</p>}
    </div>
    {right}
  </div>
);
